// custom-category.component.ts (REFACTORED - Dialog Logic Preserved)
import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, FormGroup, FormArray } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';

// Services
import { PaginationHelperService } from 'src/app/services/pagination-helper.service';
import { DebounceService } from 'src/app/core/services/debounce.service';
import { PopUpDialogService } from '../../../services/popup-dialog.service';
import { CategoryService } from './category.service';

// Constants
import { SKU_TAB_OPTIONS } from 'src/app/lib/constants/tabs.constants';
import { SKU_TABLE_CATEGORY_HEADERS, SKU_TABLE_CATEGORY_SUBHEADERS } from 'src/app/lib/constants/sku.constants';

// Validators
import { imageValidator } from '../../validators/image-validator';

@Component({
  selector: 'app-custom-category',
  templateUrl: './custom-category.component.html',
  styleUrls: ['./custom-category.component.scss']
})
export class CustomCategoryComponent implements OnInit {
  
  categoryForms: FormArray<FormGroup>;
  newCategoriesForm: UntypedFormGroup;
  searchForm: UntypedFormGroup;
  limitForm: UntypedFormGroup;

  // Data
  category_list = [];
  all_categories_for_validation = [];

  // UI State
  tabs = SKU_TAB_OPTIONS;
  tableHeader = SKU_TABLE_CATEGORY_HEADERS;
  tableSubHeader = SKU_TABLE_CATEGORY_SUBHEADERS;
  isLoading: boolean;
  
  // Pagination
  page = 1;
  size = 10;
  total_pages: any;
  current_page = 1;
  pages_array = [];
  total: any;
  
  // Search & Sort
  searchKey: any = null;
  search_keyword = '';
  limitKey: any = null;
  sort_column = "dateTime";
  sort_type: 'ASC' | 'DESC' = 'ASC';
  
  // Tracking sets
  savingRows: Set<number> = new Set();
  editingCategories: Set<number> = new Set();
  originalCategoryData: Map<number, any> = new Map();
  loadingCategories: Set<number> = new Set();

  constructor(
    private dialogRef: MatDialogRef<CustomCategoryComponent>,
    private fb: UntypedFormBuilder,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
    public paginationHelper: PaginationHelperService,
    public debounce: DebounceService,
    public dialogService: PopUpDialogService,
    public categoryService: CategoryService
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {
    // this.getPaginatedCategories().catch((error) => {
    //   console.log('error get_categories', error)
    // });

    this.fetchAllCategories().then(() => {
      this.getPaginatedCategories().catch((error) => {
        console.log('error get_categories', error)
      });
    }).catch((error) => {
      console.log('error fetching all categories', error);
      this.getPaginatedCategories().catch((err) => {
        console.log('error get_categories', err)
      });
    });
  }

  initializeForm() {
    // Search debounce
    this.searchKey = this.debounce.debounce(() => {
      this.current_page = 1;
      this.search_keyword = this.searchForm.value.search;
      this.getPaginatedCategories().catch((error) => {
        console.log('error get_categories', error)
      });;
    }, 500);

    this.searchForm = this.fb.group({
      search: ['']
    });

    // Limit debounce
    this.limitKey = this.debounce.debounce(() => {
      this.current_page = 1;
      this.size = this.limitForm.value.limit;
      this.getPaginatedCategories().catch((error) => {
        console.log('error get_categories', error)
      });;
    }, 500);

    this.limitForm = this.fb.group({
      limit: ['']
    });

    // Category forms
    this.categoryForms = this.fb.array([]);
    this.newCategoriesForm = this.fb.group({
      categories: this.categoryForms
    });
  }

  // =============================================
  // FORM MANAGEMENT
  // =============================================

  addCategoryForm() {
    const categoryForm = this.fb.group({
      itemCategory: ['', Validators.required],
      categoryDescription: ['', Validators.required],
      image: [
        null,
        [Validators.required],
        [imageValidator(
          this.categoryService.maxFileSize, 
          this.categoryService.targetImageSize.width, 
          this.categoryService.targetImageSize.height
        )]
      ],
      imageBanner: [
        null,
        [Validators.required],
        [imageValidator(
          this.categoryService.maxFileSize, 
          this.categoryService.targetBannerSize.width, 
          this.categoryService.targetBannerSize.height
        )]
      ],
      isNew: [true]
    });
    this.categoryForms.push(categoryForm);
  }

  removeCategoryForm(index: number) {
    this.dialogService.openRemoveActionDialog(
      `Are you sure you want to remove this line?`,
      () => {
        this.categoryForms.removeAt(index);
        this.savingRows.delete(index);
        this.toastr.success('Added line has been successfully removed', 'Remove Line Successful');
      },
      () => {
        console.log('Removal cancelled by user');
      }
    );
  }

  get categoryFormsArray() {
    return this.categoryForms.controls;
  }

  // =============================================
  // EDIT MODE MANAGEMENT
  // =============================================

  editCategory(category: any) {
    this.originalCategoryData.set(category.uid, { ...category });
    this.editingCategories.add(category.uid);
  }

  cancelEdit(category: any) {
    const originalData = this.originalCategoryData.get(category.uid);
    const hasChanges = this.categoryService.hasChanges(category, originalData);

    if (hasChanges) {
      this.dialogService.openInfoActionDialog(
        'You have unsaved changes. Are you sure you want to cancel?',
        () => {
          this.performCancelEdit(category);
          this.toastr.success('Changes discarded', 'Success');
        },
        () => {
          console.log('Cancel edit cancelled by user');
        }
      );
    } else {
      this.performCancelEdit(category);
    }
  }

  isEditing(categoryUid: number): boolean {
    return this.editingCategories.has(categoryUid);
  }

  // =============================================
  // FILE HANDLING
  // =============================================

  async onFileSelected(event: Event, type: 'image' | 'imageBanner', formIndex: number): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) {
      return;
    }

    // Use service for validation
    const validation = await this.categoryService.validateImage(file, type);
    
    if (!validation.valid) {
      this.toastr.error(validation.error!, 'Upload Error');
      input.value = '';
      return;
    }

    const formGroup = this.categoryForms.at(formIndex);
    formGroup.get(type)?.setValue(file);
    formGroup.get(type)?.markAsTouched();
    formGroup.get(type)?.updateValueAndValidity();
    
    this.toastr.success(`File "${file.name}" is valid and ready to upload`, 'Success File Upload');
  }

  async onExistingCategoryFileSelected(event: Event, category: any, type: 'image' | 'imageBanner'): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) {
      return;
    }

    // Use service for validation
    const validation = await this.categoryService.validateImage(file, type);
    
    if (!validation.valid) {
      this.toastr.error(validation.error!, 'Upload Error');
      input.value = '';
      return;
    }

    if (type === 'image') {
      category.newImage = file;
      category.newImageFileName = file.name;
    } else {
      category.newImageBanner = file;
      category.newImageBannerFileName = file.name;
    }
    
    this.toastr.success(`Added "${file.name}" has been successfully added`, 'Success File Upload');
  }

  getFileName(formIndex: number, controlName: string): string {
    const file = this.categoryForms.at(formIndex).get(controlName)?.value;
    return file ? file.name : '';
  }

  // =============================================
  // SAVE OPERATIONS
  // =============================================

  async saveCategory(index: number) {
    if (this.savingRows.has(index)) {
      this.toastr.warning('This category is already being saved', 'Please wait');
      return;
    }

    const formGroup = this.categoryForms.at(index);
    
    Object.keys(formGroup.controls).forEach(key => {
      formGroup.get(key)?.markAsTouched();
      formGroup.get(key)?.updateValueAndValidity();
    });
    
    await new Promise(resolve => setTimeout(resolve, 100));

    if (formGroup.invalid) {
      const errors = [];
      Object.keys(formGroup.controls).forEach(key => {
        const control = formGroup.get(key);
        if (control?.errors) {
          const errorMsg = this.getValidationError(index, key);
          if (errorMsg){ 
            errors.push(`${key}: ${errorMsg}`);
          }
        }
      });
      
      this.toastr.error(
        errors.length > 0 ? errors.join(', ') : 'Please fill in all required fields correctly!',
        'Failed to Save'
      );
      return;
    }

    const categoryData = formGroup.value;
    
    this.dialogService.openSaveActionDialog(
      `Are you sure you want to save this category "${categoryData.itemCategory}"?`,
      async () => {
        await this.performSaveCategory(index, categoryData);
      },
      () => {
        console.log('Save cancelled by user');
      }
    );
  }

  async saveCategoryInBulk() {
    if (this.categoryForms.length === 0) {
      this.toastr.warning('No categories to save', 'Warning');
      return;
    }

    // Use service for validation
    const validation = this.categoryService.validateFormArray(this.categoryForms.controls);
    await new Promise(resolve => setTimeout(resolve, 100));

    if (validation.incompleteIndices.length > 0) {
      this.toastr.error(
        `Row(s) ${validation.incompleteIndices.join(', ')} have missing values. Please fill in all required fields or remove incomplete rows.`,
        'Incomplete Forms'
      );
      return;
    }

    if (validation.invalidIndices.length > 0) {
      this.toastr.error(
        `Row(s) ${validation.invalidIndices.join(', ')} have validation errors. Please fix all errors before saving.`,
        'Validation Failed'
      );
      return;
    }

    this.dialogService.openSaveActionDialog(
      `Are you sure you want to save all ${this.categoryForms.length} ${this.categoryForms.length === 1 ? 'category' : 'categories'}?`,
      async () => {
        await this.performBulkSave();
      },
      () => {
        console.log('Bulk save cancelled by user');
      }
    );
  }


  // =============================================
  // UPDATE OPERATIONS
  // =============================================

  async updateCategory(category: any) {
    if (!category.itemCategory || !category.categoryDescription) {
      this.toastr.error('Please fill in all required fields', 'Failed to update');
      return;
    }

    const originalData = this.originalCategoryData.get(category.uid);
    const hasChanges = this.categoryService.hasChanges(category, originalData);
    
    if (!hasChanges) {
      this.toastr.success('No changes detected', 'Update without changes');
      this.cancelEdit(category);
      return;
    }

    this.dialogService.openUpdateActionDialog(
      `Are you sure you want to update this item category "${category.itemCategory}"?`,
      async () => {
        await this.performUpdateCategory(category);
      },
      () => {
        console.log('Update cancelled by user');
      }
    );
  }

  // =============================================
  // STATUS TOGGLE
  // =============================================

  async toggleCategoryStatus(
    category: any, 
    event: { newState: boolean; previousState: boolean }
  ): Promise<void> {
    const action = event.newState ? 'enable' : 'disable';
    
    const messages = {
      enable: `Please be advised that the SKUs within the selected category will become active if the category "${category.itemCategory}" is enabled`,
      disable: `Please be advised that the SKUs within the selected category will become inactive if the category "${category.itemCategory}" is disabled`,
    };

    const dialogDescription = messages[action];

    this.dialogService.openStatusActionDialog(
      dialogDescription,
      action.toUpperCase(),
      'CANCEL',
      async () => {
        this.loadingCategories.add(category.uid);
        
        try {
          this.spinner.show().catch((error) => {
            console.log('error spinner', error)
          });
          
          // Use service for API call
          const response = await this.categoryService.toggleCategoryStatus(category, action);
                    
          category.isActive = event.newState;
          
          this.dialogService.openSuccessDialog(
            `Category "${category.itemCategory}" has been ${action}d successfully!`
          );
          
          await this.getPaginatedCategories();
          
        } catch (error) {
          console.error('Error updating category status:', error);
          
          const errorMessage = error?.error?.message || error?.message || `Failed to ${action} category`;
          this.dialogService.openErrorDialog(errorMessage);
          
          category.isActive = event.previousState;
          
        } finally {
          this.spinner.hide().catch((err) => {
            console.error('Spinner hide error:', err);
          });
          this.loadingCategories.delete(category.uid);
        }
      },
      () => {
        console.log(`${action} action cancelled by user`);
      }
    );
  }

  // =============================================
  // DATA FETCHING
  // =============================================
  async fetchAllCategories(): Promise<void> {
    try {
      const params = {
        search: '',
        sortBy: {
          [this.sort_column]: this.sort_type
        }
      };
      
      // Fetch with a very large size to get all categories at once
      const response = await this.categoryService.fetchCategories(1, 999999, params);
      
      if (response.status === 200) {
        this.all_categories_for_validation = response.result.data;
      }
    } catch (error) {
      console.error('Error fetching all categories for validation:', error);
      this.all_categories_for_validation = [];
    }
  }

  async getPaginatedCategories() {
    this.spinner.show().catch((error) => {
      console.log('error spinner', error)
    });
    this.isLoading = true;
    
    const params = {
      search: this.search_keyword,
      sortBy: {
        [this.sort_column]: this.sort_type
      }
    };
    
    this.pages_array = [];

    try {
      const response = await this.categoryService.fetchCategories(
        this.current_page, 
        this.size, 
        params
      );
      
      if (response.status === 200) {
        this.category_list = response.result.data;
        this.isLoading = false;
        this.pages_array = this.paginationHelper.generatePageArray(
          this.current_page,
          response.result.totalPages
        );
        this.total_pages = response.result.totalPages;
        this.current_page = response.result.currentPage;
        this.total = response.result.totalItems;
        
        setTimeout(() => {
          this.spinner.hide().catch((err) => { 
            console.error('Spinner show error:', err); 
          });
        }, 500);
      }

    } catch (error) {
      console.error(error);
      this.toastr.error(error.error?.message || 'Something went wrong!', 'Error!');
      this.spinner.hide().catch((err) => {
        console.error('Spinner show error:', err);
      });
    }
  }

  // =============================================
  // UTILITY METHODS
  // =============================================

  getValidationError(formIndex: number, controlName: string): string {
    const control = this.categoryForms.at(formIndex).get(controlName);
    // Use service for validation error message
    return this.categoryService.getValidationError(control);
  }

  truncateFilename(filename: string, maxLength: number = 30, endChars: number = 15): string {
    return this.categoryService.truncateFilename(filename, maxLength, endChars);
  }

  formatDate(dateString: string): string {
    return this.categoryService.formatDate(dateString);
  }

  isRowSaving(index: number): boolean {
    return this.savingRows.has(index);
  }

  isCategoryEnabled(category: any): boolean {
    return !category.deleteDate;
  }

  // =============================================
  // PAGINATION & SEARCH
  // =============================================

  onSearchKeyupFromComponent(event: any): void {
    this.searchForm.patchValue({ search: event.target.value });
    this.searchKey();
  }

  onLimitChangeFromComponent(event: any): void {
    this.limitForm.patchValue({ limit: event.target.value });
    this.limitKey();
  }

  keyDownFunction(event: any) {
    if (event.keyCode === 13) {
      this.searchList();
    }
  }

  searchList() {
    this.current_page = 1;
    this.search_keyword = this.searchForm.value.search;
    this.getPaginatedCategories().catch((error) => {
      console.log('error get_categories', error)
    });
  }

  setPage(num: number) {
    this.current_page = num;
    this.getPaginatedCategories().catch((error) => {
      console.log('error get_categories', error)
    });
  }

  setFirst(num: number) {
    this.page = num;
    this.getPaginatedCategories().catch((error) => {
      console.log('error get_categories', error)
    });
  }

  nextPage() {
    if (this.current_page < Number(this.total_pages)) {
      this.current_page++;
      this.getPaginatedCategories().catch((error) => {
      console.log('error get_categories', error)
    });
    }
  }

  prevPage() {
    if (this.current_page > 1) {
      this.current_page--;
      this.getPaginatedCategories().catch((error) => {
      console.log('error get_categories', error)
    });
    }
  }

  sortList(sort_column: string, sort_type: 'ASC' | 'DESC') {
    this.spinner.show().catch((err => {
      console.error('Spinner show sort error:', err);
    }));

    const { column, type } = this.paginationHelper.toggleSort(
      this.sort_column,
      this.sort_type,
      sort_column,
      sort_type
    );

    this.sort_column = column;
    this.sort_type = type;
    
    if (this.category_list) {
      this.getPaginatedCategories().catch((error) => {
      console.log('error get_categories', error)
    });
    }
  }

  // =============================================
  // DIALOG
  // =============================================

  closeDialog(newCategory?: string) {
    this.dialogRef.close(newCategory);
  }

  // =============================================
  // PRIVATE METHODS ( PERFORM METHODS )
  // =============================================
  private trimCategoryData(categoryData: any): any {
    return {
      ...categoryData,
      itemCategory: categoryData.itemCategory?.trim() || '',
    };
  }

  private isDuplicateCategoryName(categoryName: string): boolean {
    const trimmedName = categoryName.trim().toLowerCase();
    return this.all_categories_for_validation.some(
      category => category.itemCategory.toLowerCase() === trimmedName
    );
  }

  private findDuplicateCategoryNames(categoryNames: string[]): string[] {
    const duplicates: string[] = [];
    const trimmedNames = categoryNames.map(name => name.trim());
    
    trimmedNames.forEach(name => {
      if (this.isDuplicateCategoryName(name)) {
        duplicates.push(name);
      }
    });
    
    return duplicates;
  }

  private async performSaveCategory(index: number, categoryData: any) {
    const formGroup = this.categoryForms.at(index);
    
    const ensureNoTrailingSpace = this.trimCategoryData(categoryData);
      if (this.isDuplicateCategoryName(ensureNoTrailingSpace.itemCategory)) {
      this.dialogService.openErrorDialog(
        `Category name "${ensureNoTrailingSpace.itemCategory}" already exists. Please use a different name.`
      );
      return;
    }

    const formData = this.categoryService.buildCategoryFormData(ensureNoTrailingSpace);

    this.savingRows.add(index);
    formGroup.disable();
    
    try {
      this.spinner.show().catch((error) => {
        console.log('error spinner', error)
      });
      
      const response = await this.categoryService.saveCategory(formData);
      
      
      this.dialogService.openSuccessDialog(
        `Category "${categoryData.itemCategory}" has been saved successfully!`
      );
      
      this.categoryForms.removeAt(index);
      this.savingRows.delete(index);

      await this.fetchAllCategories();
      await this.getPaginatedCategories();
      
    } catch (error) {
      console.error('Error saving category:', error);
      
      const errorMessage = error?.error?.message || error?.message || 'Failed to save category';

      this.dialogService.openErrorDialog(errorMessage);
      
      formGroup.enable();
      this.savingRows.delete(index);
    } finally {
      this.spinner.hide().catch((err) => {
        console.error('Spinner hide error:', err);
      });
    }
  }
  
  private async performBulkSave() {
    try {
      this.spinner.show().catch((error) => {
        console.log('error spinner', error)
      });

      const gamingCategories: any[] = [];
      const allImages: File[] = [];
      const allBanners: File[] = [];
      const categoryNamesToSave: string[] = [];

      // First pass: collect and trim all category names
      this.categoryForms.controls.forEach((formGroup) => {
        const categoryData = formGroup.value;
        const trimmedName = categoryData.itemCategory?.trim() || '';
        categoryNamesToSave.push(trimmedName);
      });

      // Check for duplicates with existing categories
      const duplicates = this.findDuplicateCategoryNames(categoryNamesToSave);
      
      if (duplicates.length > 0) {
        this.spinner.hide().catch((err) => {
          console.error('Spinner hide error:', err);
        });
        
        const duplicateList = duplicates.map(name => `"${name}"`).join(', ');
        this.dialogService.openErrorDialog(
          `The following category ${duplicates.length === 1 ? 'name' : 'names'} 
          already ${duplicates.length === 1 ? 'exists' : 'exist'}: 
          ${duplicateList}. Please use different names.`
        );
        return;
      }

      // Check for duplicates within the batch itself
      const nameCounts = new Map<string, number>();
      const internalDuplicates: string[] = [];
      
      categoryNamesToSave.forEach(name => {
        const lowerName = name.toLowerCase();
        const count = (nameCounts.get(lowerName) || 0) + 1;
        nameCounts.set(lowerName, count);
        
        if (count === 2) {
          internalDuplicates.push(name);
        }
      });
      
      if (internalDuplicates.length > 0) {
        this.spinner.hide().catch((err) => {
          console.error('Spinner hide error:', err);
        });
        
        const duplicateList = internalDuplicates.map(name => `"${name}"`).join(', ');
        this.dialogService.openErrorDialog(
          `Duplicate category ${internalDuplicates.length === 1 ? 'name' : 'names'} found in your batch: ${duplicateList}. Each category name must be unique.`
        );
        return;
      }

      // Second pass: build the data for saving
      this.categoryForms.controls.forEach((formGroup, index) => {
        const categoryData = formGroup.value;
        
        // Trim whitespace from text fields
        gamingCategories.push({
          itemCategory: categoryData.itemCategory?.trim() || '',
          categoryDescription: categoryData.categoryDescription?.trim() || ''
        });

        if (categoryData.image instanceof File) {
          allImages.push(categoryData.image);
        }
        
        if (categoryData.imageBanner instanceof File) {
          allBanners.push(categoryData.imageBanner);
        }

        formGroup.disable();
        this.savingRows.add(index);
      });

      // Use service to build FormData
      const formData = this.categoryService.buildBulkFormData(
        gamingCategories, 
        allImages, 
        allBanners
      );

      // Use service for API call
      const response = await this.categoryService.bulkSaveCategories(formData);


      this.dialogService.openSuccessDialog(
        `${gamingCategories.length} ${gamingCategories.length === 1 ? 'category has' : 'categories have'} been saved successfully!`
      );

      while (this.categoryForms.length > 0) {
        this.categoryForms.removeAt(0);
      }
      this.savingRows.clear();

      await this.fetchAllCategories();
      await this.getPaginatedCategories();

    } catch (error) {
      console.error('Error saving categories:', error);

      const errorMessage = error?.error?.message || error?.message || 'Failed to save categories';
      this.dialogService.openErrorDialog(errorMessage);

      this.categoryForms.controls.forEach((formGroup, index) => {
        formGroup.enable();
        this.savingRows.delete(index);
      });

    } finally {
      this.spinner.hide().catch((err) => {
        console.error('Spinner hide error:', err);
      });
    }
  }

  private async performUpdateCategory(category: any) {
    try {
      this.spinner.show().catch((error) => {
        console.log('error spinner', error)
      });
      
      const updateCategoryDetails = {
        ...category,
        itemCategory: category.itemCategory?.trim() || '',
      };
      // Use service to build FormData
      const formData = this.categoryService.buildUpdateFormData(updateCategoryDetails);
      
      const response = await this.categoryService.updateCategory(formData);
      
      
      this.dialogService.openSuccessDialog(
        `Category Item "${category.itemCategory}" has been updated successfully!`
      );
      
      this.editingCategories.delete(category.uid);
      this.originalCategoryData.delete(category.uid);
      
      delete category.newImage;
      delete category.newImageBanner;
      delete category.newImageFileName;
      delete category.newImageBannerFileName;
      
      await this.fetchAllCategories();
      await this.getPaginatedCategories();
      
    } catch (error) {
      console.error('Error updating category:', error);
      const errorMessage = error?.error?.message || error?.message || 'Failed to update category';
      this.dialogService.openErrorDialog(errorMessage);
    } finally {
      this.spinner.hide().catch((err) => {
        console.error('Spinner hide error:', err);
      });
    }
  }

  private performCancelEdit(category: any) {
    const originalData = this.originalCategoryData.get(category.uid);
    if (originalData) {
      Object.assign(category, originalData);
      this.originalCategoryData.delete(category.uid);
    }
    
    this.editingCategories.delete(category.uid);
  }

}
